import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlarmClock, Bell, MessageSquare, UserPlus, type LucideIcon } from "lucide-react";
import { useLanguage, type Language } from "../ui/LanguageProvider";

// 우측 상단 알림 벨 — 안 읽은 게 있으면 빨간 점, 누르면 목록, 항목을 누르면 그 자리로.
//
//  ★ 배지는 점이다(숫자 아님). "정확히 몇 건"보다 "새 게 있다"가 중요한 경우라서다.
//    대신 알림 종류를 4개로 제한해 빨강이 흔해지지 않게 했다 — 빨강이 흔해지면
//    지표 자체를 무시하게 된다(PatternFly: 빨강은 조치 필요에만).
//
//  ★ 읽음 처리는 "여는 순간"이 아니라 "항목을 누를 때" 한다.
//    열자마자 다 읽음이면 훑어보기만 해도 배지가 꺼져서 놓친다.
//
//  ★ 폴링은 마운트 + 탭 포커스 복귀 때만. 60초 타이머는 걸지 않는다
//    (배경 탭에서 계속 도는 요청은 소음이다).

interface NotificationItem {
  id: string;
  kind: "assigned" | "commented" | "overdue" | "remind";
  title: string;
  body?: string | null;
  link_url?: string | null;
  created_at: string;
  unread: boolean;
  derived: boolean;
}

// 종류별 아이콘 타일 색 — 예전 매핑(배정=초록 · 댓글=파랑 · 기한 지남=빨강 · 리마인드=회색)을 Vault 토큰으로.
const KIND_META: Record<NotificationItem["kind"], { Icon: LucideIcon; tile: string; label: { ko: string; en: string } }> = {
  assigned: { Icon: UserPlus, tile: "bg-success-subtle text-success-strong", label: { ko: "배정", en: "Assigned" } },
  commented: { Icon: MessageSquare, tile: "bg-primary-subtle text-primary", label: { ko: "댓글", en: "Comments" } },
  overdue: { Icon: AlarmClock, tile: "bg-danger-subtle text-danger-strong", label: { ko: "기한 지남", en: "Overdue" } },
  remind: { Icon: Bell, tile: "bg-neutral-subtle text-ink-muted", label: { ko: "리마인드", en: "Reminders" } },
};
const KIND_ORDER: NotificationItem["kind"][] = ["assigned", "commented", "overdue", "remind"];

function relTime(iso: string, language: Language = "ko"): string {
  const t = Date.parse(iso);
  if (!isFinite(t)) return "";
  const sec = Math.floor((Date.now() - t) / 1000);
  const en = language === "en";
  if (sec < 60) return en ? "Just now" : "방금";
  if (sec < 3600) return en ? `${Math.floor(sec / 60)}m ago` : `${Math.floor(sec / 60)}분 전`;
  if (sec < 86400) return en ? `${Math.floor(sec / 3600)}h ago` : `${Math.floor(sec / 3600)}시간 전`;
  if (sec < 604800) return en ? `${Math.floor(sec / 86400)}d ago` : `${Math.floor(sec / 86400)}일 전`;
  return iso.slice(0, 10);
}

export default function NotificationBell() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // 종류 탭 — 받은 알림에 있는 종류만 탭으로 만든다. "all" = 전체.
  const [kindFilter, setKindFilter] = useState<NotificationItem["kind"] | "all">("all");
  const ref = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/crm/tasks/notifications", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        setItems(d.notifications || []);
        setUnread(d.unread || 0);
      })
      .catch(() => {
        // 미로그인·권한 없음 등은 조용히 무시한다. 벨은 부가 기능이라 화면을 막지 않는다.
        setItems([]);
        setUnread(0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load]);

  // 바깥 클릭으로 닫기 — 계정 메뉴와 같은 패턴.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const markRead = (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));
    setUnread((n) => Math.max(0, n - 1));
    void fetch("/api/crm/tasks/notifications", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    }).catch(() => undefined);
  };

  const markAll = () => {
    setItems((prev) => prev.map((n) => ({ ...n, unread: false })));
    setUnread(0);
    void fetch("/api/crm/tasks/notifications", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ all: true }),
    }).catch(() => undefined);
  };

  const openItem = (n: NotificationItem) => {
    if (n.unread) markRead(n.id);
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
  };

  const kinds = KIND_ORDER.filter((k) => items.some((n) => (KIND_META[n.kind] ? n.kind : "remind") === k));
  const activeKind = kindFilter !== "all" && kinds.includes(kindFilter) ? kindFilter : "all";
  const visibleItems = activeKind === "all" ? items : items.filter((n) => (KIND_META[n.kind] ? n.kind : "remind") === activeKind);

  return (
    <div ref={ref} className="ds-scope relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unread ? t(`알림 ${unread}건`, `${unread} notifications`) : t("알림", "Notifications")}
        title={unread ? t(`안 읽은 알림 ${unread}건`, `${unread} unread notifications`) : t("알림", "Notifications")}
        className="app-context-bar__btn"
      >
        <Bell size={16} />
        {unread > 0 ? (
          <span
            aria-hidden
            className="absolute right-[7px] top-[7px] h-[7px] w-[7px] rounded-pill bg-danger"
            style={{ boxShadow: "0 0 0 2px var(--canvas)" }}
          />
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-[1200] flex max-h-[460px] w-[360px] flex-col overflow-hidden rounded-card border border-hairline-strong bg-white"
          style={{ boxShadow: "0 8px 24px rgba(31,42,68,.10)" }}
        >
          <div className="flex flex-shrink-0 items-center gap-2 border-b border-hairline px-3.5 py-3">
            <span className="text-[14px] font-semibold text-ink">{t("알림", "Notifications")}</span>
            <span className="text-[12px] text-ink-muted">
              {loading ? t("불러오는 중…", "Loading…") : <>{t("안 읽음", "Unread")} <span className="font-num tabular-nums">{unread}</span></>}
            </span>
            <div className="flex-1" />
            {unread > 0 ? (
              <button type="button" onClick={markAll} className="h-7 rounded-control px-1.5 text-[12px] font-semibold text-primary-strong hover:bg-primary-subtle">
                {t("모두 읽음", "Mark all read")}
              </button>
            ) : null}
          </div>

          {kinds.length > 1 ? (
            <div role="tablist" aria-label={t("알림 종류", "Notification type")} className="flex flex-shrink-0 gap-0.5 border-b border-hairline px-2.5 pt-2">
              {(["all", ...kinds] as Array<NotificationItem["kind"] | "all">).map((k) => {
                const on = activeKind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setKindFilter(k)}
                    className={`-mb-px h-8 border-b-2 px-2 text-[12px] ${on ? "border-primary font-semibold text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}
                  >
                    {k === "all" ? t("전체", "All") : t(KIND_META[k].label)}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {visibleItems.length ? (
              visibleItems.map((n) => {
                const meta = KIND_META[n.kind] || KIND_META.remind;
                const Icon = meta.Icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openItem(n)}
                    className={`grid w-full grid-cols-[28px_minmax(0,1fr)_auto] items-start gap-2.5 border-b border-hairline px-3.5 py-2.5 text-left last:border-b-0 hover:bg-surface-hover ${n.unread ? "bg-surface-soft" : "bg-white"}`}
                  >
                    <span aria-hidden className={`grid h-7 w-7 place-items-center rounded-card-sm ${meta.tile}`}>
                      <Icon size={14} />
                    </span>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-[13px] font-semibold leading-snug text-ink">{n.title}</span>
                      {n.body ? <span className="truncate text-[12px] text-ink-muted">{n.body}</span> : null}
                    </span>
                    <span className="whitespace-nowrap pt-0.5 font-num text-[12px] text-ink-muted">{relTime(n.created_at, language)}</span>
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center text-[13px] text-ink-muted">
                {loading ? t("불러오는 중…", "Loading…") : t("새 알림이 없습니다.", "No new notifications.")}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
